import React, { useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { useAdvisorStore } from '../../store/advisorStore';
import type { AdvisorManagement, AdvisorStatusUpdateForm } from '../../types/advisor';

interface AdvisorSuspensionModalProps {
  isOpen: boolean;
  onClose: () => void;
  advisor: AdvisorManagement;
  onSuccess: () => void;
}

export const AdvisorSuspensionModal: React.FC<AdvisorSuspensionModalProps> = ({
  isOpen,
  onClose,
  advisor,
  onSuccess
}) => {
  const { suspendAdvisor, isLoading } = useAdvisorStore();
  const [formData, setFormData] = useState<AdvisorStatusUpdateForm>({
    reason: ''
  });
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.reason.trim()) {
      setError('Le motif de suspension est requis');
      return;
    }

    if (formData.reason.trim().length < 10) {
      setError('Le motif doit contenir au moins 10 caractères');
      return;
    }

    try {
      await suspendAdvisor(advisor.id, formData);
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la suspension');
    }
  };

  const handleChange = (field: keyof AdvisorStatusUpdateForm, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="w-6 h-6 text-orange-600" />
            <h2 className="text-xl font-semibold text-gray-900">Suspendre l'accompagnateur</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Advisor Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-2">Accompagnateur</h3>
            <p className="text-gray-700">{advisor.full_name}</p>
            <p className="text-sm text-gray-600">{advisor.email}</p>
            <div className="mt-2 text-sm text-gray-600">
              <p>Projets actifs : {advisor.current_projects_count}</p>
              <p>Projets total : {advisor.active_projects_count}</p>
            </div>
          </div>

          {/* Suspension Reason */}
          <div>
            <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-2">
              Motif de la suspension *
            </label>
            <textarea
              id="reason"
              value={formData.reason}
              onChange={(e) => handleChange('reason', e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Expliquez les raisons de la suspension (minimum 10 caractères)..."
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              {formData.reason.length} / 10 caractères minimum
            </p>
          </div>

          {/* Warning */}
          <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
            <p className="text-orange-800 text-sm">
              <strong>Attention :</strong> L'accompagnateur sera suspendu et ne pourra plus accéder aux projets. 
              Ses projets actuels devront être réassignés.
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isLoading || !formData.reason.trim() || formData.reason.trim().length < 10}
              className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Suspension...</span>
                </div>
              ) : (
                'Suspendre'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};